import { useEffect } from "react";
import { track, trackOnce } from "@/lib/analytics";
import { trackConversion } from "@/hooks/use-page-view";

/**
 * Fires analytics events at scroll-depth milestones within the article body.
 * Each milestone fires at most once per article per session - mirrored to
 * Plausible/GA via trackOnce AND to our own page_views table via
 * trackConversion so the Founder Dashboard can see reading completion for
 * anonymous visitors (the vast majority).
 */
export function useReadProgress(slug: string, extraProps: Record<string, string> = {}) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    trackOnce(`read.start.${slug}`, "Article Read Start", { slug, ...extraProps });

    const path = window.location.pathname;
    const milestones: Array<{ pct: number; name: string; kind: string | null; fired: boolean }> = [
      { pct: 25, name: "Article Read 25%", kind: "read_25", fired: false },
      { pct: 50, name: "Article Read 50%", kind: "read_50", fired: false },
      { pct: 75, name: "Article Read 75%", kind: "read_75", fired: false },
      { pct: 90, name: "Article Read 90%", kind: null, fired: false },
      { pct: 100, name: "Article Complete", kind: "read_100", fired: false },
    ];

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const doc = document.documentElement;
        const scrolled = window.scrollY + window.innerHeight;
        const total = Math.max(doc.scrollHeight, 1);
        const pct = Math.min(100, (scrolled / total) * 100);
        for (const m of milestones) {
          if (!m.fired && pct >= m.pct) {
            m.fired = true;
            trackOnce(`read.${m.pct}.${slug}`, m.name, { slug, ...extraProps });
            if (m.kind) {
              // Server-side milestone - captured for anonymous visitors too.
              const sessionKey = `sa.${m.kind}.${slug}`;
              try {
                if (!window.sessionStorage.getItem(sessionKey)) {
                  window.sessionStorage.setItem(sessionKey, "1");
                  trackConversion(m.kind, path);
                }
              } catch {
                trackConversion(m.kind, path);
              }
            }
          }
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, [slug]);
}

export { track, trackOnce };
